<script setup lang="ts">
// Main game screen. Layout/CSS lifted from the prototype's #game-screen block.
// Spin flow: POST /spin → animate reels to outcome → reveal result popup.
import { ref, computed, onMounted, useTemplateRef } from 'vue';
import { useGameStore } from '../stores/game';
import { launchFireworks } from '../lib/confetti';
import {
  buildStrip,
  findSymIndex,
  findNonMatchIndex,
  STOP_TIMES,
  POST_STOP_PAUSE,
} from '../lib/spinEngine';
import ReelDrum from './ReelDrum.vue';
import ResultPopup from './ResultPopup.vue';
import HistoryPanel from './HistoryPanel.vue';
import PinDialog from './PinDialog.vue';
import type { SpinResult } from '../lib/api';

const store = useGameStore();

const reel0 = useTemplateRef<InstanceType<typeof ReelDrum>>('reel0');
const reel1 = useTemplateRef<InstanceType<typeof ReelDrum>>('reel1');
const reel2 = useTemplateRef<InstanceType<typeof ReelDrum>>('reel2');

const result = ref<SpinResult | null>(null);
const showResult = ref(false);
const showHistory = ref(false);
const showPinDialog = ref(false);
const winning = ref(false);

const strip = computed(() => buildStrip(store.paytable));

const errorMsg = ref<string | null>(null);

async function spin() {
  if (store.spinning || showResult.value) return;
  if (store.paytable.length === 0) return;

  errorMsg.value = null;
  let r: SpinResult;
  try {
    r = await store.spin();
  } catch (e: any) {
    errorMsg.value =
      e?.message === 'offline_no_tickets'
        ? 'Offline — reconnect to fetch more spins.'
        : 'Spin failed — try again.';
    return;
  }
  result.value = r;
  winning.value = false;

  let idx0: number, idx1: number, idx2: number;
  if (r.won && r.prize) {
    idx0 = findSymIndex(strip.value, r.prize.id);
    idx1 = findSymIndex(strip.value, r.prize.id);
    idx2 = findSymIndex(strip.value, r.prize.id);
  } else {
    const all = strip.value;
    idx0 = Math.floor(all.length * 0.5);
    const r0Prize = all[idx0].prizeId ?? -1;
    idx1 = findNonMatchIndex(all, r0Prize);
    idx2 = Math.floor(all.length * 0.7);
  }

  const refs = [reel0.value, reel1.value, reel2.value];
  const landings = [idx0, idx1, idx2];
  let stopped = 0;
  refs.forEach((reelRef, i) => {
    reelRef?.runAnimation(STOP_TIMES[i], landings[i], () => {
      stopped++;
      if (stopped === 3) {
        if (r.won) {
          winning.value = true;
          launchFireworks();
        }
        setTimeout(() => {
          showResult.value = true;
        }, POST_STOP_PAUSE);
      }
    });
  });
}

async function closeResult() {
  showResult.value = false;
  winning.value = false;
  await store.refreshPaytable();
}

function onAdminUnlock() {
  showPinDialog.value = false;
  store.enterAdmin();
}

onMounted(() => {
  store.refreshPaytable();
});
</script>

<template>
  <div id="game-screen">
    <div id="gbg">
      <img v-if="store.config?.event.bg_image_url" :src="store.config.event.bg_image_url" alt="" />
    </div>

    <div class="gcont">
      <div class="ghdr">
        <div class="glogo-img">
          <img v-if="store.config?.event.logo_image_url" :src="store.config.event.logo_image_url" alt="WSB" />
        </div>
        <div class="gear-row">
          <div v-if="!store.online" class="status-pill" :class="{ blocked: store.ticketsRemaining === 0 }">
            <span class="dot"></span>
            <span class="lbl">{{ store.ticketsRemaining > 0 ? `OFFLINE · ${store.ticketsRemaining}` : 'OFFLINE' }}</span>
          </div>
          <div class="gear" @click="showHistory = true" title="History">📋</div>
          <div class="gear" @click="showPinDialog = true" title="Admin">⚙</div>
        </div>
      </div>

      <div v-if="errorMsg" class="err-toast">{{ errorMsg }}</div>

      <div class="machine-wrap">
        <div class="cabinet" :class="{ winning }">
          <div class="cab-top">
            <img v-if="store.config?.event.title_image_url" :src="store.config.event.title_image_url" alt="Worldy's Hit N' Win" class="title-img" />
            <span v-else class="cab-title-text">🎰 WORLDY'S HIT N' WIN 🎰</span>
          </div>

          <div class="reels-row">
            <ReelDrum ref="reel0" :strip="strip" />
            <ReelDrum ref="reel1" :strip="strip" />
            <ReelDrum ref="reel2" :strip="strip" />
          </div>

          <div class="win-line-label">⎯⎯ WIN LINE ⎯⎯</div>

          <div class="cab-bot">
            <button
              class="spin-btn"
              :disabled="store.spinning || showResult || store.paytable.length === 0 || (!store.online && store.ticketsRemaining === 0)"
              @click="spin"
            >
              {{ store.spinning ? '...' : 'SPIN' }}
            </button>
          </div>
        </div>
      </div>

      <div class="tbl-wrap">
        <div class="tbl-title">⎯⎯ PRIZE TABLE — MATCH 3 ⎯⎯</div>
        <div class="paytable-grid">
          <div v-for="p in store.paytable" :key="p.id" class="pt-card" :class="{ out: p.qty_remaining === 0 }">
            <div class="pt-sym">
              <img v-if="p.image_url" :src="p.image_url" :alt="p.name" />
              <span v-else class="pt-e">{{ p.emoji }}</span>
            </div>
            <div class="pt-info">
              <div class="pt-name">{{ p.name }}</div>
              <div class="pt-match">MATCH 3</div>
            </div>
            <div class="pt-rate">{{ p.win_rate }}%</div>
          </div>
        </div>
      </div>
    </div>

    <ResultPopup v-if="showResult && result" :result="result" @close="closeResult" />
    <HistoryPanel v-if="showHistory" @close="showHistory = false" />
    <PinDialog v-if="showPinDialog" @close="showPinDialog = false" @unlock="onAdminUnlock" />
  </div>
</template>

<style scoped>
#game-screen {
  flex-direction: column;
  min-height: 100vh;
  position: relative;
  overflow: hidden;
}
#gbg {
  position: fixed;
  inset: 0;
  background: linear-gradient(160deg, #0f1114 0%, #0a0a0b 55%, #0f1114 100%);
  z-index: 0;
}
#gbg img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.3;
}
.gcont {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  padding-bottom: 8px;
}
.ghdr {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 14px;
  background: rgba(0, 0, 0, 0.6);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(4px);
}
.glogo-img {
  display: flex;
  align-items: center;
  min-height: 38px;
}
.glogo-img img {
  height: 38px;
}
.gear-row {
  display: flex;
  gap: 8px;
  align-items: center;
}
.gear {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 17px;
  color: #fff;
}
.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px;
  border-radius: 14px;
  background: rgba(255, 200, 60, 0.15);
  border: 1px solid rgba(255, 200, 60, 0.4);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1px;
  color: #ffd86b;
}
.status-pill.blocked {
  background: rgba(255, 51, 4, 0.18);
  border-color: rgba(255, 51, 4, 0.5);
  color: #ff8266;
}
.status-pill .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  box-shadow: 0 0 6px currentColor;
}
.err-toast {
  margin: 8px auto 0;
  max-width: 420px;
  width: calc(100% - 16px);
  padding: 10px 14px;
  background: rgba(255, 51, 4, 0.15);
  border: 1px solid rgba(255, 51, 4, 0.5);
  border-radius: 8px;
  color: #ff8266;
  font-size: 13px;
  text-align: center;
}

.machine-wrap {
  display: flex;
  justify-content: center;
  padding: 10px 0 0;
}
.cabinet {
  width: 100%;
  background: transparent;
  position: relative;
  display: flex;
  flex-direction: column;
}
.cab-top {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0 8px;
}
.title-img {
  display: block;
  max-height: 52px;
  max-width: 80%;
  object-fit: contain;
}
.cab-title-text {
  color: #fff;
  font-size: 13px;
  font-weight: 900;
  letter-spacing: 3px;
  text-transform: uppercase;
  text-shadow: 0 0 20px rgba(255, 51, 4, 0.6);
}

.reels-row {
  display: flex;
  gap: 0;
  align-items: stretch;
  border: 1px solid rgba(120, 60, 0, 0.35);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 0 40px rgba(0, 0, 0, 0.8), inset 0 0 60px rgba(0, 0, 0, 0.4);
  margin: 0 auto;
  background: #0a0505;
  max-width: 420px;
  width: calc(100% - 16px);
  max-height: 390px;
}

.win-line-label {
  text-align: center;
  font-size: 8px;
  color: rgba(255, 255, 255, 0.3);
  letter-spacing: 3px;
  text-transform: uppercase;
  padding: 5px 0 2px;
}

.cab-bot {
  padding: 12px 8px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.spin-btn {
  width: 240px;
  height: 68px;
  border-radius: 16px;
  background: linear-gradient(135deg, var(--or-lt) 0%, var(--or) 50%, var(--or-dk) 100%);
  border: none;
  box-shadow: 0 4px 20px rgba(255, 51, 4, 0.55), 0 1px 0 rgba(255, 255, 255, 0.25) inset,
    0 -2px 0 rgba(0, 0, 0, 0.3) inset;
  font-size: 22px;
  font-weight: 900;
  color: #fff;
  letter-spacing: 4px;
  cursor: pointer;
  text-transform: uppercase;
  transition: transform 0.1s, box-shadow 0.1s;
  outline: none;
  user-select: none;
  flex-shrink: 0;
}
.spin-btn:active:not(:disabled) {
  transform: scale(0.96) translateY(2px);
  box-shadow: 0 2px 8px rgba(255, 51, 4, 0.3);
}
.spin-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

@keyframes winGlow {
  0%,
  100% {
    box-shadow: 0 0 15px rgba(255, 51, 4, 0.4);
  }
  50% {
    box-shadow: 0 0 50px rgba(255, 51, 4, 0.95), 0 0 100px rgba(255, 51, 4, 0.3);
  }
}
.cabinet.winning .reels-row {
  animation: winGlow 0.5s ease-in-out 3;
}

/* ── PAYTABLE ── */
.tbl-wrap {
  width: 100%;
  margin: 8px 0 0;
  padding: 0 8px 8px;
}
.tbl-title {
  font-size: 9px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.45);
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 8px;
  text-align: center;
}
.paytable-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 6px;
}
.pt-card {
  background: rgba(0, 0, 0, 0.45);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 8px 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 4px;
  position: relative;
  overflow: hidden;
}
.pt-card.out {
  opacity: 0.4;
}
.pt-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.04) 0%, transparent 60%);
  pointer-events: none;
}
.pt-sym {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.pt-sym img {
  width: 36px;
  height: 36px;
  object-fit: contain;
  filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.7));
}
.pt-sym .pt-e {
  font-size: 28px;
  line-height: 1;
}
.pt-info {
  flex: 1;
  min-width: 0;
}
.pt-name {
  font-size: 10px;
  font-weight: 700;
  color: #fff;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
}
.pt-match {
  font-size: 9px;
  color: rgba(255, 255, 255, 0.35);
  letter-spacing: 0.3px;
}
.pt-rate {
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  color: #fff;
  background: rgba(255, 51, 4, 0.25);
  border: 1px solid rgba(255, 51, 4, 0.4);
  border-radius: 4px;
  padding: 1px 6px;
}
</style>
